/**
 * Global Fetch Interceptor
 * 
 * Intercepts all fetch calls and automatically adds Authorization header
 * with the Permission Token (Token #2) for authenticated API requests.
 * 
 * This interceptor:
 * - Adds Authorization header to requests to API endpoints
 * - Skips adding headers for FusionAuth requests (they use their own auth)
 * - Auto-refreshes tokens if expiring within threshold
 * - Handles 401 responses by refreshing token and retrying once
 */

import { authService } from '@/services/authService';
import { env } from '@/config/env';

// Store original fetch
const originalFetch = globalThis.fetch;

/**
 * Check if URL is an API endpoint that needs authentication
 */
function isApiEndpoint(url: string): boolean {
  try {
    const urlObj = new URL(url, globalThis.location.origin);
    
    // Skip FusionAuth endpoints - they handle their own authentication
    if (urlObj.href.includes(env.authServiceUrl)) {
      return false;
    }
    
    // Include all other service endpoints
    if (
      urlObj.href.includes(env.apiBaseUrl) ||
      urlObj.href.includes(env.assessmentServiceBaseUrl) ||
      urlObj.href.includes(env.complianceRequirementsServiceBaseUrl) ||
      urlObj.href.includes(env.evidenceServiceBaseUrl) ||
      urlObj.href.includes(env.locationServiceBaseUrl) ||
      urlObj.href.includes(env.userServiceBaseUrl) ||
      urlObj.href.includes(env.authorizationServiceUrl) ||
      urlObj.href.includes(env.vehicleServiceBaseUrl)
    ) {
      return true;
    }
    
    return false;
  } catch {
    // If URL parsing fails, assume it's not an API endpoint
    return false;
  }
}

/**
 * Merge headers from Request and RequestInit
 */
function mergeHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  if (input instanceof Request) {
    // Start with headers from the Request object
    const headers = new Headers(input.headers);
    // Merge init headers on top (if provided)
    if (init?.headers) {
      const initHeaders = new Headers(init.headers);
      initHeaders.forEach((value, key) => {
        headers.set(key, value);
      });
    }
    return headers;
  }
  
  // For string/URL input, only use init headers
  return new Headers(init?.headers);
}

/**
 * Handle 401 response by refreshing token and retrying
 */
async function handle401Response(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  headers: Headers,
  originalResponse: Response
): Promise<Response> {
  console.log('[FetchInterceptor] Got 401, attempting token refresh...');
  
  try {
    await authService.refreshPermissionToken();
    const newToken = await authService.getValidPermissionToken();
    
    if (!newToken) {
      console.error('[FetchInterceptor] Token refresh failed');
      return originalResponse;
    }
    
    // Retry with new token
    headers.set('Authorization', `Bearer ${newToken}`);
    const retryInit: RequestInit = { ...init, headers };
    const retryResponse = await originalFetch(input, retryInit);
    
    if (retryResponse.status === 401) {
      console.error('[FetchInterceptor] Still got 401 after refresh, session expired');
      await authService.logout();
    }
    
    return retryResponse;
  } catch (error) {
    console.error('[FetchInterceptor] Token refresh error:', error);
    return originalResponse;
  }
}

/**
 * Intercepted fetch function
 */
async function interceptedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Extract URL string from input
  let url: string;
  if (typeof input === 'string') {
    url = input;
  } else if (input instanceof URL) {
    url = input.href;
  } else {
    url = input.url;
  }
  
  // If not an API endpoint, use original fetch
  if (!isApiEndpoint(url)) {
    return originalFetch(input, init);
  }
  
  // If authorization is disabled, make calls without Authorization header
  const shouldUseAuth = env.authEnabled || env.authorizationEnabled;
  if (!shouldUseAuth) {
    return originalFetch(input, init);
  }
  
  // Merge headers from input and init
  const headers = mergeHeaders(input, init);
  
  // Check if Authorization header is already set
  if (headers.has('Authorization')) {
    return originalFetch(input, init);
  }
  
  // Get valid permission token (auto-refreshes if needed)
  try {
    const token = await authService.getValidPermissionToken();
    
    if (!token) {
      console.warn('[FetchInterceptor] No valid permission token available');
      return originalFetch(input, init);
    }
    
    // Add Authorization header
    headers.set('Authorization', `Bearer ${token}`);
    const modifiedInit: RequestInit = { ...init, headers };
    
    // Make request
    const response = await originalFetch(input, modifiedInit);
    
    // Handle 401 by refreshing token and retrying once
    if (response.status === 401) {
      return handle401Response(input, init, headers, response);
    }
    
    return response;
  } catch (error) {
    console.error('[FetchInterceptor] Error in fetch interceptor:', error);
    return originalFetch(input, init);
  }
}

/**
 * Install the fetch interceptor
 */
export function installFetchInterceptor(): void {
  if (globalThis.fetch === interceptedFetch) {
    console.warn('[FetchInterceptor] Already installed');
    return;
  }
  
  globalThis.fetch = interceptedFetch;
  console.log('[FetchInterceptor] Installed successfully');
}

/**
 * Uninstall the fetch interceptor (for testing)
 */
export function uninstallFetchInterceptor(): void {
  globalThis.fetch = originalFetch;
  console.log('[FetchInterceptor] Uninstalled');
}
